import { Stack, Typography } from '@mui/joy';
import { ICellRendererParams } from 'ag-grid-community';
import { t } from 'i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Tooltip } from 'core/elements';
import { Direction, Level } from 'theme/enums';

export const DeliveredViaCell = ({ data, valueFormatted }: ICellRendererParams) => {
  // Group rows have no data and keep the plain (possibly empty) label.
  if (!data) {
    return <>{valueFormatted}</>;
  }

  // No delivery recorded here, but the extraction plainly persisted: another
  // instance handled it. Saying so beats three blanks that read as "nothing
  // happened". Read off `deliveredVia` rather than the formatted value, because
  // the formatter now produces this label too (so exports and the filter carry
  // it) and would otherwise route these rows past the tooltip.
  const isProcessedElsewhere = !data.deliveredVia && !!data.processedElsewhere;

  const label = isProcessedElsewhere ? (
    <Tooltip title={t('tooltipProcessedElsewhere')}>
      <Typography level={Level.BODY_SM} sx={{ color: 'neutral.500' }}>
        {t('labelProcessedElsewhere')}
      </Typography>
    </Tooltip>
  ) : (
    <>{valueFormatted || DEFAULT_PLACEHOLDER}</>
  );

  // Defusing is orthogonal to how the row was delivered, so it annotates the
  // label rather than replacing it — an upload handled elsewhere can still have
  // defused its own poll loop.
  if (!data.pollDefused) {
    return label;
  }

  return (
    <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 0.5, height: '100%' }}>
      {label}
      <Tooltip title={t('tooltipPollDefused')}>
        <Typography level={Level.BODY_XS} sx={{ color: 'neutral.500' }}>
          ({t('labelPollDefused')})
        </Typography>
      </Tooltip>
    </Stack>
  );
};
